import React, { FC } from 'react';

import Picture from 'components/picture';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import { Typography } from 'components/ui';
import { t } from 'state/intl/translations';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { parseStringifiedJSON } from 'utils/parse-string';

import { PriceAndCalories } from './price-and-calories';
import { SelectionsList } from './selections-list';
import { ProductSkeleton } from './skeleton';
import {
  DetailsContainer,
  ImageContainer as ImageContainerBase,
  Layout,
  ProductDescription,
  ProductName,
  QuantityBadge,
  Wrapper,
} from './styled';
import { ProductProps } from './types';

/**
 *
 * Product is used to display a compact product to guests
 * Anywhere we need to show a menu item outside the menu we should use this component
 *
 */
export const Product: FC<React.PropsWithChildren<ProductProps>> = ({
  image,
  fallbackImage,
  name,
  price,
  calories,
  selections,
  quantity,
  loading = false,
  displayMode = 'list',
  imageSize,
  isProductNameBold = false,
  description,
  quantityBadgeTextStyle,
  quantityBadgeStyle,
  fontFamily,
}) => {
  // Should not render the product if the name is empty and is not loading
  if (!name && !loading) {
    return null;
  }

  const isInlineLayout = displayMode === 'list' || !!selections?.length;

  return (
    <Wrapper testID="product">
      {loading ? (
        <ProductSkeleton />
      ) : (
        <Layout $inline={isInlineLayout}>
          {(image || fallbackImage) && (
            <ImageContainerBase key={name} $size={imageSize} testID="product-image">
              {image && (
                <Picture
                  image={parseStringifiedJSON({ value: image })}
                  alt={`${t('product')} ${name}`}
                  accessibilityHidden
                  objectFitContain
                />
              )}
              {!image &&
                fallbackImage &&
                fallbackImage({
                  width: 55,
                  height: 55,
                })}
              {quantity && (
                <QuantityBadge {...quantityBadgeStyle}>
                  <Typography
                    variant="copyTwo"
                    style={quantityBadgeTextStyle}
                    {...hiddenAccessibilityPlatformProps}
                  >
                    {quantity}x
                  </Typography>
                  <VisuallyHidden accessibilityLabel={t('xItems', { xItems: quantity })} />
                </QuantityBadge>
              )}
            </ImageContainerBase>
          )}
          <DetailsContainer>
            <ProductName
              fontFamily={fontFamily as 'heading' | 'body' | undefined}
              testID="product-name"
            >
              {name}
            </ProductName>
            {description && <ProductDescription>{description}</ProductDescription>}
            <PriceAndCalories price={price} calories={calories} />
            {!!selections?.length && <SelectionsList selections={selections} id={name || ''} />}
          </DetailsContainer>
        </Layout>
      )}
    </Wrapper>
  );
};
